import Vue from 'vue';
import { NodeType } from '@amplify/amp-module';

import NodeService from '../node-service';
import TriggerService from '../trigger-service';
import { endpoints } from '../../constants/endpoints';
import { objectToSortedArray, isObjectEmpty } from '../../utils';

export default class ChatbotService {
  constructor(ctx) {
    this.ctx = ctx;
    this.realtime = ctx.$realtime;
    this.triggers = null;
    this.nodeService = new NodeService(ctx);
    this.triggers = new TriggerService(this.ctx, this.nodeService);
    this.store = ctx.store;
  }

  /**
   * Fetch the published amp on the current domain
   * @param password
   * @returns true if the amp is published or false otherwise
   */
  async fetchInitialAmp(password = null) {
    // hide edetail error page
    this.store.dispatch('chatbotStore/setSelfdetailerErrorPage', false);

    try {
      // if the domain is protected we need to pass the password as a body parameter
      const edetailPassword = localStorage.getItem('edetailPassword');
      const accessPassword = password || edetailPassword;
      const body = { ...(accessPassword && { password: accessPassword }) };

      if (password && password !== edetailPassword) {
        // update the edetail password in the local storage
        localStorage.setItem('edetailPassword', password);
      }

      this.store.dispatch('chatbotStore/setIsAmpLoading', true);
      const {
        data: { ampId, data, domains, version, crmConnector },
      } = await this.ctx.$amplifyApi.post(endpoints.amp.published(), body);

      if (crmConnector) {
        this.store.dispatch('chatbotStore/setCrmConnector', crmConnector);
        this.publishToCrmChannel();
      }

      this.store.dispatch('chatbotStore/setIsAmpLoading', false);

      // hide password page
      this.store.dispatch('chatbotStore/setPasswordPage', false);

      // In case the API returns an empty object - then we have nothing to render
      if (!data || isObjectEmpty(data)) {
        this.store.dispatch('chatbotStore/clearAmpHistory');

        // show edetail error page
        this.store.dispatch('chatbotStore/setSelfdetailerErrorPage', true);

        return true;
      }

      // Closing current amp if it isn't the same as the requested one
      const rootAmp = this.store.getters['chatbotStore/rootAmp'];

      if (rootAmp && rootAmp?.id !== ampId) {
        this.store.dispatch('chatbotStore/clearAmpHistory');
      }

      this.store.dispatch('chatbotStore/pushAmp', { ...data, domains, ampVersion: version });

      return true;
    } catch (error) {
      this.store.dispatch('chatbotStore/setIsAmpLoading', false);

      const { response: { data: { statusCode } = {} } = {} } = error;

      if (statusCode === 401) {
        // show password page and remove stored edetail password
        this.store.dispatch('chatbotStore/setPasswordPage', true);
        localStorage.removeItem('edetailPassword');

        return false;
      }

      // eslint-disable-next-line no-console
      console.error(error);

      this.store.dispatch('chatbotStore/clearAmpHistory');

      // show edetail error page
      this.store.dispatch('chatbotStore/setSelfdetailerErrorPage', true);

      return false;
    }
  }

  getCrmAccountIdentifier() {
    const connector = this.store.state.chatbotStore?.crmConnector;
    let value = null;

    if (connector) {
      const cookieKey = connector.accountIdentifier.find(
        (identifier) => identifier.identifierType === 'COOKIE'
      )?.identifierKey;

      const paramKey = connector.accountIdentifier.find(
        (identifier) => identifier.identifierType === 'URL_PARAMETER'
      )?.identifierKey;

      /**
       * We first search for a cookei in the CRM configuration
       * If we find one we look through the cookies in the browser
       * to find a match for the provided key.
       * If no cookie identifier is set, then we look for URL params.
       */
      if (cookieKey) {
        const cookies = document.cookie.split(';').map((cookie) => {
          const [key, value] = cookie.split('=').map((part) => part.trim());
          return { key, value };
        });
        value = cookies.find((cookie) => cookie.key === cookieKey)?.value;
      }

      if (paramKey && !value) {
        const urlParams = new URLSearchParams(window.location.search);
        value = urlParams.get(paramKey);
      }
    }

    return value;
  }

  async publishToCrmChannel() {
    const unwatch = this.store.watch(
      (state, getters) => {
        return {
          ablyReady: state.chatbotStore.ablyReady,
          currentAmp: getters['chatbotStore/currentAmp'],
        };
      },
      async ({ currentAmp, ablyReady }) => {
        if (ablyReady) {
          // Waiting for next tick to avoid 410 errors on the Ably connection
          await Vue.nextTick();

          const identifier = this.getCrmAccountIdentifier();
          const clientId = localStorage.getItem('clientId');

          if (identifier && currentAmp.id && clientId) {
            const channel = [currentAmp.id, clientId, 'crm', 'data'].join(':');
            this.realtime.publishToCrmChannel(channel, currentAmp.organizationId, identifier);

            this.subscribeToCrmAccountInfo(identifier);
          } else {
            this.store.dispatch('chatbotStore/setCrmLoading', false);
          }

          unwatch();
        }
      }
    );
  }

  subscribeToCrmAccountInfo(identifier) {
    const clientId = localStorage.getItem('clientId');
    const currentAmp = this.store.getters['chatbotStore/currentAmp'];

    if (identifier && currentAmp.id) {
      const channel = [currentAmp.id, clientId, 'crm'].join(':');

      this.realtime.subscribeToOrganizationChannel(
        {
          channel,
          topic: 'response',
          cb: (message) => {
            Object.entries(message?.data?.accountInfo || {}).forEach(([key, value]) => {
              if (key !== 'Id') {
                this.store.dispatch('chatbotStore/addToStorage', { key, data: value, crm: true });
              }
            });

            this.store.dispatch('chatbotStore/setCrmAccountId', identifier);
            this.store.dispatch('chatbotStore/setCrmLoading', false);
          },
        },
        currentAmp.organizationId
      );

      this.realtime.subscribeToOrganizationChannel(
        {
          channel,
          topic: 'error',
          cb: (message) => {
            console.warn('Error getting CRM account information', message?.data?.errors);
            this.store.dispatch('chatbotStore/setCrmLoading', false);
          },
        },
        currentAmp.organizationId
      );
    } else {
      this.store.dispatch('chatbotStore/setCrmLoading', false);
    }
  }

  setupPreviewAmp(amp, conversationId, nodeId) {
    this.store.dispatch('chatbotStore/setPreviewAmp', amp);
    this.store.dispatch('chatbotStore/setIsPreviewRunning', !!amp && !!conversationId);
    this.store.dispatch('chatbotStore/pushAmp', amp);

    if (conversationId) {
      this.runConversation(conversationId, { nodeId });
    }
  }

  waitForCrmData() {
    return new Promise((resolve) => {
      const { crmConnector, crmLoading } = this.store.state.chatbotStore;

      if (!crmConnector || (crmConnector && !crmLoading)) {
        resolve();
        return;
      }

      const unwatch = this.store.watch(
        (state) => state.chatbotStore.crmLoading,
        (loading) => {
          if (!loading) {
            resolve();
            unwatch();
          }
        }
      );
    });
  }

  async setupAmp(stackedAmp) {
    await this.waitForCrmData();

    this.triggers.activateTriggers();

    const { stack, ampHistory } = this.store.state.chatbotStore;
    const ampStack = stack[stackedAmp.id];
    const setupOptions = this.store.getters['chatbotStore/ampOptions'];

    /**
     * We know that we can only stack one web amp
     * and an unlimited amount of edetailer amps.
     * If @var ampType is edetailer and the corresponding stack
     * length is 0, it means we stacked an amp and we need to start
     * the first conversation by default.
     */

    if (stackedAmp.ampType === 'edetailer' && !ampStack.length && ampHistory.length === 1) {
      const conversations = objectToSortedArray(stackedAmp.data?.conversations ?? {});
      this.runConversation(conversations[0].id);
      return;
    }

    /**
     * In case we have @var setupOptions not null, it means
     * that we triggered a new conversation that was part of
     * a different amp. So now we need to run it from the start.
     */
    if (setupOptions) {
      this.store.dispatch('chatbotStore/setAmpSetupOptions', null);
      if (stackedAmp.id === setupOptions.ampId) {
        this.runConversation(setupOptions.conversationId);
        return;
      }
    }

    /**
     * Here we handle if the user went back to the previous amp in the stack.
     * We search for @var lastNode in the amp stack and try to resume the conversation.
     */
    const lastNode = ampStack[ampStack.length - 1];

    if (lastNode) {
      if (
        lastNode.delayed ||
        lastNode.delayedButtons ||
        ([NodeType.question, NodeType.buttonGroup].includes(lastNode.node?.type) && !lastNode.selectedButtonId) ||
        lastNode.node?.type === NodeType.embedded ||
        lastNode.node?.type === NodeType.player ||
        (lastNode.node?.type === NodeType.form && !lastNode.formCompleted)
      ) {
        // Handle the question and buttonGroup nodes if it's the last and it's unanswered
        this.runConversation(lastNode.conversation, { nodeId: lastNode.node.id, addToStack: false });
      } else {
        this.runConversation(lastNode.conversation, { prevNodeId: lastNode.node.id });
      }
    }
  }

  clearState() {
    this.store.dispatch('chatbotStore/clearState');
  }

  clearAmpHistory() {
    this.store.dispatch('chatbotStore/clearAmpHistory');
  }

  runConversation(conversationId, options = {}) {
    const { nodeId = undefined, prevNodeId = undefined, addToStack = true } = options;
    const isPreview = this.store.getters['chatbotStore/isPreview'];
    const { ampType } = this.store.getters['chatbotStore/currentAmp'];
    const latestConversationId = this.store.getters['chatbotStore/getLatestConversationId'];
    const conversation = this.store.getters['chatbotStore/getConversation'](conversationId);

    if (!conversation) {
      return;
    }

    if (ampType === 'edetailer' && !isPreview) {
      this.store.dispatch('chatbotStore/setIframeUrl', conversationId);
    }

    this.store.dispatch('chatbotStore/setupConversation', conversation);

    const node = nodeId
      ? this.store.getters['chatbotStore/getNode'](nodeId)
      : this.store.getters['chatbotStore/nextNode'](prevNodeId);

    if (!node) {
      // End current conversation if there isn't a node to run
      this.store.dispatch('chatbotStore/endConversation');
      return;
    }

    const currentSlide = this.store.getters['chatbotStore/getSlide'](latestConversationId);
    const nextSlide = this.store.getters['chatbotStore/getSlide'](conversationId);

    // slides are not available in preview mode and
    // we don't need to wait for the slide to load when the new conversation has the same slide
    if (ampType === 'edetailer' && !isPreview && currentSlide !== nextSlide) {
      this.unwatch = this.store.watch(
        (state) => state.chatbotStore.isSlideLoading,
        (newValue) => {
          if (!newValue) {
            this.unwatch();
            this.nodeService.runNode(node, addToStack);
          }
        }
      );

      return;
    }

    this.nodeService.runNode(node, addToStack);
  }
}
